/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { useContext, useEffect, useRef } from 'react';

import { cssMap, jsx } from '@compiled/react';

import { fg } from '@atlaskit/platform-feature-flags/fg';
import { token } from '@atlaskit/tokens';

import { TopNavStartAttachRef } from '../../../context/top-nav-start/top-nav-start-attach-ref';

/**
 * Styles for the TopNavStart element.
 */

const innerStyles = cssMap({
	root: {
		// Taking up the full height of top bar to allow for monitoring mouse events, for improving the side nav flyout experience
		height: '100%',
		alignItems: 'center',
		display: 'flex',
		gap: token('space.050'),
		gridColumn: 1,
		// Sets a minimum width on the content so that it aligns with the side nav (when side nav is at default width)
	},
	container: {
		width: 'var(--n_tnSideWidth, auto)',
		minWidth: 'var(--n_tnStartMinWidth, 0px)',
		boxSizing: 'border-box',
		'@media (min-width: 64rem)': {
			width: 'var(--n_tnSideWidth, max-content)',
			minWidth: 'var(--n_tnStartMinWidth, 330px)',
		},
	},
	viewport: {
		'@media (min-width: 64rem)': {
			// Intrinsic width of content without wrapping
			// The actual grid column can still be larger
			width: 'max-content',
			/**
			 * Using `300px` because:
			 *
			 * - Side nav default width is `320px`
			 * - Top bar has `12px` padding on each side
			 * - There is an `8px` gap between TopNavStart and TopNavMiddle
			 *
			 * So `320px` - (`12px` + `8px`) = `300px`
			 * Jira product logo update, added 30px for CSM logo as the size is larger than the default
			 * https://jplat.atlassian.net/browse/BLU-8440
			 * So `300px` + `30px` = `330px`
			 */
			minWidth: '330px',
			// We want the specified width to be inclusive of padding
			boxSizing: 'border-box',
		},
	},
});

type TopNavStartProps = {
	/**
	 * The content of the layout area.
	 *
	 * Should contain `SideNavToggleButton`, `AppSwitcher`, and `AppLogo`/`CustomLogo` components.
	 */
	children: React.ReactNode;
	/**
	 * A unique string that appears as data attribute `data-testid` in the rendered code, serving as a hook for automated tests.
	 */
	testId?: string;
	/**
	 * Slot for the side nav toggle button.
	 *
	 * You should only render `<SideNavToggleButton>` inside this slot, not as a child.
	 *
	 * Consumers that do not need a toggle button can explicitly pass `null`.
	 */
	sideNavToggleButton: React.ReactNode;
};

/**
 * __TopNavStart__
 *
 * Wrapper for the top navigation actions on the inline-start (left) side of the top navigation.
 */
export function TopNavStart({
	children,
	testId,
	sideNavToggleButton,
}: TopNavStartProps): JSX.Element {
	const ref = useContext(TopNavStartAttachRef);
	const elementRef = useRef(null);

	// FIXME: unsafe pattern with Suspense, should use callback ref / store in state
	// Should handle the underlying HTMLElement changing without a remount
	useEffect(() => {
		ref(elementRef.current);
	}, [elementRef, ref]);

	return (
		<div
			ref={elementRef}
			data-testid={testId}
			css={[
				innerStyles.root,
				fg('platform-dst-chat-panel-layout') ? innerStyles.container : innerStyles.viewport,
			]}
		>
			{sideNavToggleButton}
			{children}
		</div>
	);
}
